"use client"

import type { ReactNode } from "react"
import Sidebar from "./sidebar"
import TopNav from "./top-nav"
import CommandPalette from "./command-palette"
import MobileDock from "./mobile-dock"
import SentryFeedbackPositioner from "../sentry-feedback-positioner"
import { useTheme } from "next-themes"
import { useEffect, useState } from "react"
import { SidebarProvider, useSidebar } from "@/contexts/sidebar-context"
import { CommandPaletteProvider } from "@/contexts/command-palette-context"

interface LayoutProps {
  children: ReactNode
}

function LayoutShell({ children }: LayoutProps) {
  const { theme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const { menuState, isHovered, isMobile } = useSidebar()

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  // Margin reserves canvas room for the floating sidebar.
  // = sidebar left offset (12px) + sidebar width
  // Hidden / mobile: 0 (sidebar is out of flow or overlay-only).
  const marginLeft = (() => {
    if (isMobile) return "0"
    if (menuState === "hidden") return "0"
    if (menuState === "collapsed" && isHovered) return "268px" // 12 + 256
    if (menuState === "collapsed") return "76px"               // 12 + 64
    return "268px"                                              // 12 + 256
  })()

  return (
    <div className={`flex h-screen bg-background ${theme === "dark" ? "dark" : ""}`}>
      <Sidebar />
      <div
        className="w-full flex flex-1 flex-col min-w-0 transition-[margin] duration-300 ease-in-out lg:gap-3"
        style={{ marginLeft }}
      >
        <div className="flex-shrink-0 lg:px-3 lg:pt-3 relative z-10">
          <header
            className="
              flex items-center h-16 overflow-hidden
              bg-panel border border-panel-border
              lg:rounded-2xl lg:shadow-float
              max-lg:border-x-0 max-lg:border-t-0
            "
          >
            <TopNav />
          </header>
        </div>
        <main
          className="flex-1 overflow-auto bg-background min-w-0 p-3 sm:p-6 lg:pt-0 pb-[calc(env(safe-area-inset-bottom,0px)+5.5rem)] lg:pb-6"
        >
          {children}
        </main>
      </div>

      <MobileDock />
      <CommandPalette />
      <SentryFeedbackPositioner />
    </div>
  )
}

export default function Layout({ children }: LayoutProps) {
  return (
    <SidebarProvider>
      <CommandPaletteProvider>
        <LayoutShell>{children}</LayoutShell>
      </CommandPaletteProvider>
    </SidebarProvider>
  )
}
